import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Faux-3D tilt container. Tracks the pointer and applies a subtle
 * rotateX / rotateY with a specular glare overlay for a "milled crystal" feel.
 * GPU-accelerated (transform only) — safe for 60fps.
 */
export function TiltCard({ children, className = "", max = 7, glare = true, testId, ...rest }) {
    const ref = useRef(null);
    const px = useMotionValue(0);
    const py = useMotionValue(0);

    const sx = useSpring(px, { stiffness: 220, damping: 22, mass: 0.4 });
    const sy = useSpring(py, { stiffness: 220, damping: 22, mass: 0.4 });

    const rotateX = useTransform(sy, [-0.5, 0.5], [max, -max]);
    const rotateY = useTransform(sx, [-0.5, 0.5], [-max, max]);
    const glareX = useTransform(sx, [-0.5, 0.5], ["0%", "100%"]);
    const glareY = useTransform(sy, [-0.5, 0.5], ["0%", "100%"]);
    const glareBg = useTransform(
        [glareX, glareY],
        ([x, y]) => `radial-gradient(240px circle at ${x} ${y}, rgb(var(--accent-1-rgb) / 0.16), transparent 60%)`
    );

    const handleMove = (e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        px.set((e.clientX - r.left) / r.width - 0.5);
        py.set((e.clientY - r.top) / r.height - 0.5);
    };
    const handleLeave = () => {
        px.set(0);
        py.set(0);
    };

    return (
        <motion.div
            ref={ref}
            data-testid={testId}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d", transformPerspective: 900 }}
            className={`relative will-change-transform ${className}`}
            {...rest}
        >
            {children}
            {glare && (
                <motion.span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{ background: glareBg }}
                />
            )}
        </motion.div>
    );
}
